import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons'
import {
  Select,
  Tooltip,
  Collapse,
  Box,
  Flex,
  Text,
  Title,
  Radio,
} from '@mantine/core'
import React, { useEffect, useMemo, useState } from 'react'

import { useTranslation } from '@flecto/translations'
import {
  type CoreItemPriceRule,
  type CoreItem,
  type CoreItemPriceRuleDurationUnit,
} from '@flecto/types'
import { Icon, InputCurrency } from '@flecto/ui'

import { useTaxesInfo } from '../../../taxes'
import { useItemLowestPriceRule } from '../../hooks/useItemLowestPriceRule'
import styles from './styles.module.css'

export type OvertimeChargeProps = {
  item: CoreItem
  onChangeOvertime: (
    overtime: boolean,
    overtime_rule?: CoreItemPriceRule
  ) => void
  error?: boolean
}

export function OvertimeCharge({
  item,
  onChangeOvertime,
  error,
}: OvertimeChargeProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'items' })
  const {
    userSettingShowPricesWithTaxes,
    userDefaultTaxBracket,
    userDefaultTaxable,
    getCalculatedAmounts,
  } = useTaxesInfo()
  const [selected, setSelected] = useState<boolean>(
    item?.overtime_enabled ?? false
  )
  const [customIndex, setCustomIndex] = useState<number>(
    item?.pricing_rule_overtime?.duration_unit === 'hours' ? 0 : 1
  )
  const [initialRule, setInitialRule] = useState<CoreItemPriceRule | null>()
  const [customRule, setCustomRule] = useState<{
    price_cents: number
    price_cents_with_tax: number
    duration_unit: CoreItemPriceRuleDurationUnit
  }>({
    price_cents: 0,
    price_cents_with_tax: 0,
    duration_unit: 'hours',
  })

  const [previousTaxRate, setPreviousTaxRate] = useState<string | null>(null)
  const [previousTaxable] = useState<boolean | null>(null)

  const transformUnit = (item: Partial<CoreItemPriceRule>) => {
    const durationValue = item.duration_value ?? 0

    const units = {
      days:
        durationValue <= 1
          ? t('overtime_charge.day')
          : t('overtime_charge.days'),
      hours:
        durationValue <= 1
          ? t('overtime_charge.hour')
          : t('overtime_charge.hours'),
    }

    return item.duration_unit ? units[item.duration_unit] : ''
  }

  const minimalIntervalRule = useItemLowestPriceRule({ item })

  const itemMinimalOption = useMemo(() => {
    return {
      value: minimalIntervalRule
        ? `${minimalIntervalRule.duration_value} ${minimalIntervalRule.duration_unit}`
        : '',
      label: minimalIntervalRule
        ? `${minimalIntervalRule.duration_value} ${transformUnit({
            duration_unit: minimalIntervalRule.duration_unit,
            duration_value: minimalIntervalRule.duration_value,
          })}`
        : '',
    }
  }, [item.pricing_rules])

  const minimalAmount = getCalculatedAmounts(
    minimalIntervalRule?.price_cents || 0,
    item.tax_rate,
    item.taxable
  )

  const centsDefault = getCalculatedAmounts(
    1000000,
    userDefaultTaxBracket.value,
    userDefaultTaxable,
    true
  )

  const defaultCentsCalculated: CoreItemPriceRule = {
    duration_unit: 'days',
    duration_value: 1,
    price_cents: centsDefault.totalWithoutTaxes.cents,
  }

  const defaultRules = useMemo(() => {
    const comesFromMinimal =
      item.pricing_rule_overtime?.price_cents === undefined
    const priceCents = selected
      ? comesFromMinimal
        ? initialRule?.price_cents
        : item.pricing_rule_overtime?.price_cents > 0
          ? item.pricing_rule_overtime?.price_cents
          : initialRule?.price_cents
      : initialRule?.price_cents

    const unit = comesFromMinimal
      ? initialRule?.duration_unit
      : item.pricing_rule_overtime?.duration_unit

    return [
      {
        duration_value: 1,
        duration_unit: 'hours' as CoreItemPriceRuleDurationUnit,
        price_cents:
          unit === 'hours' ? priceCents : defaultCentsCalculated.price_cents,
      },
      {
        duration_value: 1,
        duration_unit: 'days' as CoreItemPriceRuleDurationUnit,
        price_cents:
          unit === 'days' ? priceCents : defaultCentsCalculated.price_cents,
      },
    ]
  }, [item.pricing_rule_overtime, item.overtime_enabled])

  const itensOption = defaultRules?.map((rule, index) => ({
    value: `${index}`,
    label: `${rule.duration_value} ${transformUnit(rule)}`,
  }))

  const handleChangePriceWithoutTaxes = (index: number, cents: number) => {
    const amounts = getCalculatedAmounts(
      cents,
      item.tax_rate,
      item.taxable,
      true
    )

    onChangeOvertime(true, {
      duration_value: 1,
      duration_unit: defaultRules[index].duration_unit,
      price_cents: amounts.totalWithoutTaxes.cents,
    })
  }

  const handleChangePriceWithTaxes = (index: number, cents: number) => {
    const amounts = getCalculatedAmounts(
      cents,
      item.tax_rate,
      item.taxable,
      true,
      true
    )

    onChangeOvertime(true, {
      duration_value: 1,
      duration_unit: defaultRules[index].duration_unit,
      price_cents: amounts.totalWithoutTaxes.cents,
    })
  }

  useEffect(() => {
    setInitialRule(item.pricing_rule_overtime)
  }, [])

  useEffect(() => {
    if (!selected) {
      onChangeOvertime(false, {} as CoreItemPriceRule)
    }
  }, [selected])

  useEffect(() => {
    const previousAmounts = getCalculatedAmounts(
      defaultRules[customIndex]?.price_cents || 0,
      previousTaxRate || item.tax_rate,
      previousTaxable || item.taxable
    )

    const amounts = getCalculatedAmounts(
      userSettingShowPricesWithTaxes
        ? previousAmounts.totalWithTaxes.cents
        : previousAmounts.totalWithoutTaxes.cents,
      item.tax_rate,
      item.taxable,
      true,
      userSettingShowPricesWithTaxes ? true : false
    )

    setPreviousTaxRate(item.tax_rate)

    onChangeOvertime(true, {
      price_cents: amounts.totalWithoutTaxes.cents,
      duration_value: 1,
      duration_unit: customRule?.duration_unit,
    })
  }, [item.tax_rate, item.taxable, customRule.duration_unit])

  useEffect(() => {
    if (selected) {
      const amounts = getCalculatedAmounts(
        item.pricing_rule_overtime?.price_cents || 0,
        item.tax_rate,
        item.taxable
      )

      const rules = {
        price_cents: amounts.totalWithoutTaxes.cents,
        price_cents_with_tax: amounts.totalWithTaxes.cents,
        duration_unit: defaultRules[customIndex]?.duration_unit,
      }

      setCustomRule({
        ...rules,
      })
    }
  }, [item.pricing_rule_overtime, item.tax_rate, item.taxable, customIndex])

  return (
    <Box mt='xl'>
      <Title order={4}>{t('overtime_charge.title')}</Title>
      <Text color='dimmed' mb='md'>
        {t('overtime_charge.description')}
      </Text>
      <Flex>
        <Radio.Group
          value={`${selected}`}
          onChange={(value) => {
            const newSelected = value === 'true'
            setSelected(newSelected)
            const comesFromMinimal =
              item.pricing_rule_overtime?.price_cents === undefined
            if (newSelected) {
              setCustomIndex(0)
              onChangeOvertime(newSelected, {
                price_cents: comesFromMinimal
                  ? defaultCentsCalculated.price_cents
                  : initialRule?.price_cents,
                duration_value: 1,
                duration_unit: 'hours',
              } as CoreItemPriceRule)
            }
          }}
          name='ruleType'
          size='md'
        >
          <Radio
            fw='500'
            my='md'
            value={'false'}
            label={
              <Tooltip
                label={
                  <Box my='sm'>
                    <Text fw={500} size='md'>
                      {t('overtime_charge.info')}
                    </Text>
                  </Box>
                }
                multiline
              >
                <Flex align='center' gap='sm' style={{ cursor: 'default' }}>
                  {t('overtime_charge.minimal_title')}
                  <Icon icon={faQuestionCircle} size='md' />
                </Flex>
              </Tooltip>
            }
          />
          <Collapse in={selected === false}>
            <Flex ml='xl' mb='lg' className={styles.linePriceRule}>
              <Select
                data-testid={'input-select-item-minimal'}
                defaultValue={itemMinimalOption.value}
                data-cy='step2-price_rule'
                disabled
                data={[itemMinimalOption]}
                value={itemMinimalOption.value}
                label={t('overtime_charge.per_extra')}
              />
              <InputCurrency
                data-testid={'input-value-minimal'}
                disabled
                value={minimalAmount.totalWithoutTaxes.cents}
                label={t('overtime_charge.value')}
              />
              <InputCurrency
                data-testid={'input-value-taxes-minimal'}
                disabled
                value={minimalAmount.totalWithTaxes.cents}
                label={t('overtime_charge.value_with_taxes')}
              />
            </Flex>
          </Collapse>

          <Radio
            fw='500'
            value='true'
            label={
              <Flex direction={'column'}>
                <Text fw={500} size='md'>
                  {t('overtime_charge.customize_title')}
                </Text>
                <Text fw='normal' color='dimmed' size='sm'>
                  {t('overtime_charge.customize_title_description')}
                </Text>
              </Flex>
            }
            my='md'
          />
          <Collapse in={selected === true}>
            <Flex ml='xl' mb='lg' className={styles.linePriceRule}>
              <Select
                data-testid={'input-select-item-customize'}
                data={itensOption}
                value={itensOption[customIndex].value}
                label={
                  <Flex gap='xs'>
                    <Text size='md'>{t('overtime_charge.per_extra')}</Text>
                  </Flex>
                }
                onChange={(value) => {
                  const index = Number(value || '0')
                  setCustomIndex(index)
                }}
              />
              <InputCurrency
                data-testid={'input-value-customize'}
                disabled={userSettingShowPricesWithTaxes}
                value={customRule.price_cents}
                error={!userSettingShowPricesWithTaxes && error}
                label={
                  <Flex gap='xs'>
                    <Text size='md'>{t('overtime_charge.value')}</Text>
                  </Flex>
                }
                onChange={(value) => {
                  handleChangePriceWithoutTaxes(customIndex, value)
                }}
              />
              <InputCurrency
                data-testid={'input-value-taxes-customize'}
                disabled={!userSettingShowPricesWithTaxes}
                error={userSettingShowPricesWithTaxes && error}
                value={customRule.price_cents_with_tax}
                label={
                  <Flex gap='xs'>
                    <Text size='md'>
                      {t('overtime_charge.value_with_taxes')}
                    </Text>
                  </Flex>
                }
                onChange={(value) => {
                  handleChangePriceWithTaxes(customIndex, value)
                }}
              />
            </Flex>
          </Collapse>
        </Radio.Group>
      </Flex>
    </Box>
  )
}
